import type { User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { AccessProfile, CreatorAccess, OrganizationAccess, OrganizationRole, PermissionGrant } from '../types';

interface OrganizationUserRow {
  organization_id: string;
  role: OrganizationRole;
  orgs: { name: string } | Array<{ name: string }> | null;
}

interface PartnerRow {
  id: string;
  display_name: string;
  tier: string;
}

interface PermissionRow { permission_key: string; organization_id: string | null; organization_name: string | null }

function relatedName(value: OrganizationUserRow['orgs']): string {
  if (Array.isArray(value)) return value[0]?.name ?? 'Sponsored Program';
  return value?.name ?? 'Sponsored Program';
}

export async function resolveAccess(user: User): Promise<AccessProfile> {
  const [staffResult, orgResult, creatorResult, permissionResult] = await Promise.all([
    supabase.rpc('is_staff'),
    supabase.from('organization_users').select('organization_id, role, orgs(name)').eq('user_id', user.id),
    supabase.from('partners').select('id, display_name, tier').eq('user_id', user.id).maybeSingle(),
    supabase.rpc('my_effective_permissions'),
  ]);

  if (staffResult.error) throw new Error(`Staff role could not be resolved: ${staffResult.error.message}`);
  if (orgResult.error) throw new Error(`Organization roles could not be resolved: ${orgResult.error.message}`);
  if (creatorResult.error) throw new Error(`Creator role could not be resolved: ${creatorResult.error.message}`);

  const organizations: OrganizationAccess[] = ((orgResult.data ?? []) as OrganizationUserRow[]).map((row) => ({
    organizationId: row.organization_id,
    organizationName: relatedName(row.orgs),
    role: row.role,
  }));
  const creatorRow = creatorResult.data as PartnerRow | null;
  const creator: CreatorAccess | null = creatorRow ? {
    partnerId: creatorRow.id,
    displayName: creatorRow.display_name,
    tier: creatorRow.tier,
  } : null;
  // Rollout fallback: the RBAC migration deploys first. If the read RPC is not
  // present yet, preserve the current fixed-role behavior instead of locking out.
  const permissions: PermissionGrant[] = permissionResult.error ? [] :
    ((permissionResult.data ?? []) as PermissionRow[]).map((row) => ({
      key: row.permission_key,
      organizationId: row.organization_id,
    }));
  if (!permissionResult.error) {
    for (const row of (permissionResult.data ?? []) as PermissionRow[]) {
      if (row.organization_id && !organizations.some((entry) => entry.organizationId === row.organization_id)) {
        organizations.push({
          organizationId: row.organization_id,
          organizationName: row.organization_name ?? 'Sponsored Program',
          role: 'analyst_viewer',
          roleLabel: 'Custom role',
        });
      }
    }
  }

  return {
    userId: user.id,
    email: user.email ?? '',
    isStaff: staffResult.data === true,
    permissions,
    organizations,
    creator,
  };
}
